import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### Fannie Mae LAR 89 Report: Discontinuance of Mortgage Insurance
 *
 * The LAR 89 report is an 80-character fixed length string. Character lengths and positions are noted at the individual fields.
 *
 * From Fannie's docs:
 *
 * The servicer must notify Fannie Mae about an automatic termination or a borrower-initiated cancellation of
 * mortgage insurance coverage via a Transaction Type 89. The termination or cancellation must be reported by
 * the last reporting day of the reporting period in which the effective date of the termination occurs.
 * The reason for the MI Discontinuance is distinguished by action codes. The proper action code that must be
 * reported differs based on whether the MI is automatically terminated or is canceled based on the borrower’s
 * request to do so.
 * Action codes are further described in the following table.
 *
 * **Action Codes:**
 * - `51`: report a borrower-initiated cancellation based on the original value of the property (or, in the case
 *       of a second-lien mortgage loan, the value of the property at the time the second-lien mortgage loan was originated)
 * - `52`: report a borrower-initiated cancellation based on the current appraised value of the property
 * - `53`: report an automatic termination (regardless of whether the termination is based on the scheduled LTV
 *       ratio or the date that is the mid-point of the amortization period).
 * - `54`: report termination due to high risk.
 */
export const FannieMaeLar89V1 = z.object({
  version: z.literal('1').describe('version'),
  /**
   * ##### Lender Number
   * Numeric length 9; characters 1 - 9
   */
  lenderNumber: z.string().describe('string'),
  /**
   * ##### Investor
   * Always `'F'` for Freddie; character 10
   */
  investor: z.literal('F').describe('literal'),
  /**
   * ##### Record Identifier
   * Always `'89'`; characters 11 - 12
   */
  recordIdentifier: z.literal('89').describe('literal'),
  /**
   * ##### Source Code
   * Always `'0'`; character 13
   */
  sourceCode: z.literal('0').describe('literal'),
  /**
   * ##### Fannie Mae Loan Number
   * Numeric length 10; characters 14 - 23
   */
  fannieMaeLoanNumber: z.string().describe('string'),
  /**
   * ##### Action Codes
   * - `51`: report a borrower-initiated cancellation based on the original value of the property (or, in the case
   *       of a second-lien mortgage loan, the value of the property at the time the second-lien mortgage loan was originated)
   * - `52`: report a borrower-initiated cancellation based on the current appraised value of the property
   * - `53`: report an automatic termination (regardless of whether the termination is based on the scheduled LTV
   *       ratio or the date that is the mid-point of the amortization period).
   * - `54`: report termination due to high risk.
   *
   * length 2; characters 24 - 25
   */
  actionCode: z.enum(['51', '52', '53', '54']).describe('enum'),
  /**
   * ##### Action Date
   * `MMDDYY` format; characters 26 - 31
   */
  actionDate: zodDateOrString.describe('date'),
  /**
   * The report ends with blanks or zeroes, length 49; characters 32 - 80
   */
});
export type FannieMaeLar89V1 = z.infer<typeof FannieMaeLar89V1>;

export const FannieMaeLar89 = FannieMaeLar89V1;
export type FannieMaeLar89 = z.infer<typeof FannieMaeLar89>;
// Make `FannieMaeLar89` a discriminated union on 'version' when changes happen
// export const FannieMaeLar89V2 = z.object({
//   version: z.literal('2'),
//   foo: z.string(),
// });
// export const FannieMaeLar89 = z.discriminatedUnion('version', [FannieMaeLar89V1, FannieMaeLar89V02012025]);
